// Orders — the one screen that answers "where is every order?"
// Three tabs: All inquiries (the whole funnel) · Pending (won but not fully
// delivered — on hold / ready / partly sent) · Delivered (fully dispatched).
// Money comes from the shared payment summary, NET of returns — the same
// numbers as the Payments and Dispatch screens, never recomputed here.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiPackage,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiShoppingCart,
  FiTruck,
  FiUser,
  FiX,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiMessageSquare,
  FiDollarSign,
  FiChevronRight,
  FiAlertCircle,
  FiTrash2,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, PageHeader } from '../components/common/index.js';
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../config/api.config';

const formatPrice = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const shortDate = (value) => String(value || '').split(' at ')[0];

const TABS = [
  { value: 'all', label: 'All inquiries', icon: FiShoppingCart },
  { value: 'pending', label: 'Pending', icon: FiClock },
  { value: 'delivered', label: 'Delivered', icon: FiCheckCircle },
];

// Funnel stage badges for the All tab (stage comes from the backend).
const STAGE_META = {
  pending: { label: 'Pending quote', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  live: { label: 'Live', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  waiting: { label: 'Waiting stock', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  hold: { label: 'On Hold', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  won: { label: 'Sold', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  loss: { label: 'Lost', cls: 'bg-rose-50 text-rose-600 border-rose-200' },
};

const SourceTag = ({ source }) => {
  if (source !== 'cap' && source !== 'osm') return null;
  const cap = source === 'cap';
  return (
    <span className={`rounded px-1 py-px text-[9px] font-bold uppercase ${cap ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`}>
      {source}
    </span>
  );
};

// Delivery state chip — derived from status + per-part progress.
const DeliveryChip = ({ order }) => {
  const prog = order.dispatch_progress || {};
  if (order.status === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
        <FiCheckCircle className="h-3.5 w-3.5" /> Delivered {shortDate(order.dispatched_at)}
      </span>
    );
  }
  if (prog.partial) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
        <FiPackage className="h-3.5 w-3.5" /> {prog.dispatched}/{prog.total} delivered
      </span>
    );
  }
  if (order.bucket === 'ready') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700">
        <FiTruck className="h-3.5 w-3.5" /> Ready to dispatch
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700">
      <FiClock className="h-3.5 w-3.5" /> On hold — payment to confirm
    </span>
  );
};

// One order/inquiry card. `funnel` = the All tab (stage badge, lighter info).
// `onDelete` (optional) renders the superadmin trash — kept off shared views.
export const OrderCard = ({ order, funnel, onOpen, onDelete }) => {
  const pay = order.payment;
  const stage = STAGE_META[order.stage] || null;
  const parts = order.parts || [];
  const balanceOpen = pay && pay.type === 'advance' && !pay.balance_paid && Number(pay.balance_due) > 0;
  const won = order.status === 'confirmed' || order.status === 'completed';
  // Parts the supervisor handed to the courier (receipt uploaded) awaiting admin approval.
  const readyToApprove = parts.filter((p) => p.sellable && p.delivery_stage === 'shipped' && !p.dispatched).length;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(order)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(order); } }}
      className="w-full cursor-pointer rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:border-violet-300 hover:shadow"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${order.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : won ? 'bg-violet-50 text-violet-600' : 'bg-slate-100 text-slate-500'}`}>
            {order.status === 'completed' ? <FiCheckCircle className="h-5 w-5" /> : won ? <FiTruck className="h-5 w-5" /> : <FiUser className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-slate-900">{order.customer_name || 'Customer'}</h3>
            <p className="mt-0.5 truncate text-sm text-slate-500">{order.car}</p>
            <p className="mt-0.5 text-xs font-medium text-slate-400">
              #{order.inquiry_id} · by {order.salesperson_name || '-'} · {shortDate(order.created_at)}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {funnel && stage ? (
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${stage.cls}`}>
              {stage.label}
            </span>
          ) : (
            <>
              <DeliveryChip order={order} />
              {readyToApprove > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-bold text-white shadow-sm">
                  <FiCheckCircle className="h-3.5 w-3.5" /> {readyToApprove} ready to approve
                </span>
              )}
            </>
          )}
          {/* Money — always NET of returns (same as Payments screen) */}
          {pay ? (
            <p className="text-sm font-black text-slate-900 tabular-nums">{formatPrice(pay.total) || '—'}</p>
          ) : order.total ? (
            <p className="text-sm font-black text-slate-400 tabular-nums">{formatPrice(order.total)}</p>
          ) : null}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(order); }}
              title="Delete this order (kept in Deleted Records)"
              className="rounded-lg border border-slate-200 p-1.5 text-slate-300 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
            >
              <FiTrash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Payment decomposition — paid / balance / returned */}
      {pay && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs">
          <span className="font-semibold text-emerald-700">Paid {formatPrice(pay.paid) || '₹0'}</span>
          {balanceOpen && (
            <span className="inline-flex items-center gap-1 font-semibold text-amber-600">
              <FiCreditCard className="h-3 w-3" /> Balance {formatPrice(pay.balance_due)} due
            </span>
          )}
          {pay.type === 'advance' && pay.balance_paid && (
            <span className="font-semibold text-emerald-600">Fully paid (advance + balance)</span>
          )}
          {pay.returned && Number(pay.returned) > 0 && (
            <span className="inline-flex items-center gap-1 font-semibold text-rose-600">
              <FiRotateCcw className="h-3 w-3" /> Returned −{formatPrice(pay.returned)}
              {pay.gross_total && <span className="font-normal text-slate-400">(was {formatPrice(pay.gross_total)})</span>}
            </span>
          )}
          {!pay.confirmed && (
            <span className="font-semibold text-orange-600">⏳ awaiting admin confirmation</span>
          )}
        </div>
      )}

      {/* Parts — delivered ticked, waiting flagged, returned struck out */}
      {parts.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {parts.filter((p) => p.sellable).map((p) => (
            <span
              key={p.id}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${p.dispatched ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}
            >
              {p.dispatched ? <FiCheckCircle className="h-3 w-3" /> : <FiClock className="h-3 w-3 text-slate-300" />}
              {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
              <SourceTag source={p.source} />
            </span>
          ))}
          {parts.filter((p) => p.returned || p.osm_unavailable).map((p) => {
            const label = p.osm_unavailable || p.return_state === 'osm_unavailable' ? 'OSM unavailable'
              : p.return_state === 'cancelled' ? 'Cancelled'
              : p.return_state === 'exchanged' ? 'Exchanged' : 'Returned';
            return (
              <span
                key={p.id}
                className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-600 line-through"
                title={`${label} — not delivered`}
              >
                <FiRotateCcw className="h-3 w-3" />
                {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
                <span className="rounded bg-rose-100 px-1 py-px text-[9px] font-bold uppercase no-underline">{label}</span>
              </span>
            );
          })}
          {parts.filter((p) => !p.sellable && !p.returned).map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-400"
              title="Not available — not part of the sale"
            >
              <FiX className="h-3 w-3" />
              {p.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Inquiry detail modal (unchanged data source — redux inquiry detail) ─────

export const DetailModal = ({ detail, loading, error, onClose }) => {
  const inquiry = detail?.inquiry;
  const history = detail?.customer_history ?? [];
  const historyCount = detail?.customer_history_count ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b border-slate-100 bg-white p-5">
          <div>
            <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-violet-600">
              {inquiry ? `Inquiry #${inquiry.id}` : 'Inquiry Detail'}
            </p>
            <h2 className="text-lg font-bold text-slate-900">{inquiry?.car_model ?? '…'}</h2>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            <p className="text-sm text-slate-400">Loading detail…</p>
          </div>
        )}

        {error && !loading && (
          <div className="m-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        )}

        {inquiry && !loading && (
          <div className="space-y-5 p-5">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Customer</p>
              <div className="space-y-2.5 rounded-xl bg-slate-50 p-4">
                <div className="flex items-center gap-2.5">
                  <FiUser className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-800">{inquiry.customer.name}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <FiPhone className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="text-sm text-slate-600">{inquiry.customer.phone_number}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <FiMapPin className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="text-sm text-slate-600">{inquiry.customer.location}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Parts Requested</p>
              <div className="flex flex-wrap gap-2">
                {String(inquiry.car_parts || '').split(',').filter(Boolean).map((p) => (
                  <span key={p.trim()} className="rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700">
                    {p.trim()}
                  </span>
                ))}
              </div>
            </div>

            {inquiry.notes && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Customer Notes</p>
                <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
                  <p className="flex gap-2 text-sm text-slate-700">
                    <FiMessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                    {inquiry.notes}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="mb-1 text-xs text-slate-400">Salesperson</p>
                <p className="text-sm font-semibold text-slate-800">{inquiry.salesperson_name}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="mb-1 text-xs text-slate-400">Quoted By</p>
                <p className="text-sm font-semibold text-slate-800">{inquiry.quoted_by?.name || '—'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-xl p-3 ${inquiry.quoted_price ? 'border border-emerald-100 bg-emerald-50' : 'bg-slate-50'}`}>
                <p className="mb-1 flex items-center gap-1 text-xs text-slate-400"><FiDollarSign className="h-3 w-3" /> Quoted Price</p>
                <p className={`text-sm font-bold ${inquiry.quoted_price ? 'text-emerald-700' : 'text-slate-400'}`}>
                  {inquiry.quoted_price ? `₹${parseFloat(inquiry.quoted_price).toLocaleString('en-IN')}` : 'Not quoted'}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="mb-1 text-xs text-slate-400">Supervisor Notes</p>
                <p className="text-sm text-slate-700">{inquiry.supervisor_notes || '—'}</p>
              </div>
            </div>

            {historyCount > 0 && (
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Other Inquiries by this Customer
                  <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">{historyCount}</span>
                </p>
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.id} className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 p-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold leading-tight text-slate-800">{h.car_model}</p>
                        <p className="mt-0.5 truncate text-xs text-slate-400">{h.car_parts}</p>
                        <p className="mt-0.5 text-xs text-slate-400">{h.salesperson_name} · {shortDate(h.created_at)}</p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-600">{h.status}</span>
                        {h.quoted_price && (
                          <span className="text-xs font-bold text-emerald-600">
                            ₹{parseFloat(h.quoted_price).toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 border-t border-slate-100 pt-1 text-xs text-slate-400">
              <FiCalendar className="h-3.5 w-3.5" />
              <span>Created: {inquiry.created_at}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Approval modal — payment + delivery proof + dispatch, all in one act ────
// The supervisor packed and handed the parts to the courier (receipt uploaded).
// Admin reviews the reported payment together with that proof and approves:
// this confirms the payment AND dispatches the delivered parts at once.

const STAGE_CHIP = {
  shipped: { label: 'Handed to courier', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  packed: { label: 'Packed', cls: 'border-amber-200 bg-amber-50 text-amber-700' },
  pending: { label: 'Not packed', cls: 'border-slate-200 bg-slate-50 text-slate-500' },
};

// One deliverable part with its OWN proof: the supervisor's pack photo(s) plus
// the courier receipt — each clickable to zoom. tone colours the row by stage.
export const ProofRow = ({ p, onZoom, tone = 'pending' }) => {
  const chip = STAGE_CHIP[p.delivery_stage] || STAGE_CHIP.pending;
  const packs = p.pack_image_urls || [];
  const toneCls =
    tone === 'ready' ? 'border-emerald-100 bg-emerald-50/40'
      : tone === 'done' ? 'border-slate-100 bg-white'
        : 'border-slate-200 bg-white';
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneCls}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {p.dispatched
            ? <FiCheckCircle className="h-4 w-4 shrink-0 text-emerald-500" />
            : p.delivery_stage === 'shipped'
              ? <FiTruck className="h-4 w-4 shrink-0 text-emerald-500" />
              : <FiClock className="h-4 w-4 shrink-0 text-slate-300" />}
          <span className="truncate text-sm font-semibold text-slate-800">
            {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
          </span>
          <SourceTag source={p.source} />
        </div>
        {p.dispatched ? (
          <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Dispatched</span>
        ) : (
          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${chip.cls}`}>{chip.label}</span>
        )}
      </div>
      {/* Proof thumbnails — pack photo(s) then the courier receipt */}
      {(packs.length > 0 || p.courier_receipt_url) ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {packs.map((url, i) => (
            <button key={url + i} type="button" onClick={() => onZoom(url)} title={`Pack photo ${i + 1}`} className="relative">
              <img src={url} alt="pack" className="h-12 w-12 rounded-lg border border-slate-200 object-cover" />
              <span className="absolute bottom-0 left-0 rounded-tr rounded-bl-lg bg-slate-900/70 px-1 text-[8px] font-bold uppercase text-white">Pack</span>
            </button>
          ))}
          {p.courier_receipt_url && (
            <button type="button" onClick={() => onZoom(p.courier_receipt_url)} title="Courier receipt" className="relative">
              <img src={p.courier_receipt_url} alt="receipt" className="h-12 w-12 rounded-lg border border-slate-200 object-cover" />
              <span className="absolute bottom-0 left-0 rounded-tr rounded-bl-lg bg-emerald-600/85 px-1 text-[8px] font-bold uppercase text-white">Rcpt</span>
            </button>
          )}
        </div>
      ) : (
        <p className="mt-1 text-[11px] italic text-slate-400">No photos yet</p>
      )}
    </div>
  );
};

export const ApprovalModal = ({ order, onClose, onApproved }) => {
  const pay = order.payment;
  const [note, setNote] = useState('');
  const [address, setAddress] = useState(order.dispatch_address || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [zoom, setZoom] = useState(null);

  const parts = order.parts || [];
  const sellable = parts.filter((p) => p.sellable);
  const voided = parts.filter((p) => p.returned || p.is_cancelled || p.osm_unavailable);
  const shipped = sellable.filter((p) => p.delivery_stage === 'shipped' && !p.dispatched);
  const alreadyOut = sellable.filter((p) => p.dispatched);
  // Sellable but the supervisor hasn't handed these over yet (e.g. an OSM part
  // still being sourced) — they are NOT part of this approval.
  const awaiting = sellable.filter((p) => p.delivery_stage !== 'shipped' && !p.dispatched);
  const noPayment = !pay;
  const canApprove = !noPayment && shipped.length > 0;

  const approve = async () => {
    setBusy(true);
    setErr('');
    try {
      await axiosInstance.post(API_ENDPOINTS.ORDERS.APPROVE(order.inquiry_id), {
        note: note.trim() || undefined,
        // Send the (possibly edited) delivery address so dispatch has it on record
        dispatch_address: address.trim() || undefined,
      });
      onApproved();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Could not approve this order');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b border-slate-100 bg-white p-5">
          <div>
            <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-violet-600">
              Approve order #{order.inquiry_id}
            </p>
            <h2 className="text-lg font-bold text-slate-900">{order.customer_name || 'Customer'}</h2>
            <p className="text-sm text-slate-500">{order.car}</p>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          {/* 1 — Payment */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">1 · Payment</p>
            {noPayment ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
                No payment reported yet — record the payment on the Payments screen first.
              </div>
            ) : (
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Total</span>
                  <span className="text-sm font-black text-slate-900">{formatPrice(pay.total) || '—'}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-sm text-slate-500">Paid ({pay.type || '—'})</span>
                  <span className="text-sm font-bold text-emerald-700">{formatPrice(pay.paid) || '₹0'}</span>
                </div>
                {pay.type === 'advance' && Number(pay.balance_due) > 0 && (
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-sm text-slate-500">Balance due</span>
                    <span className="text-sm font-bold text-amber-600">{formatPrice(pay.balance_due)}</span>
                  </div>
                )}
                <div className={`mt-2 rounded-lg px-3 py-1.5 text-xs font-bold ${pay.confirmed ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                  {pay.confirmed
                    ? '✓ Payment confirmed'
                    : `Reported by ${pay.reported_by || 'sales'} — approving will confirm it (SOLD)`}
                </div>
              </div>
            )}
          </div>

          {/* 2 — Delivery proof */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">2 · Delivery &amp; proof</p>
              {shipped.length > 0 && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                  {shipped.length} ready to approve
                </span>
              )}
            </div>

            {/* Delivered — handed to courier with pack + receipt proof; approved now */}
            {shipped.length > 0 && (
              <div className="space-y-1.5">
                {shipped.map((p) => <ProofRow key={p.id} p={p} onZoom={setZoom} tone="ready" />)}
              </div>
            )}

            {/* Already dispatched in an earlier approval */}
            {alreadyOut.length > 0 && (
              <div className="mt-1.5 space-y-1.5">
                {alreadyOut.map((p) => <ProofRow key={p.id} p={p} onZoom={setZoom} tone="done" />)}
              </div>
            )}

            {/* Not delivered yet — the supervisor hasn't handed these over. They
                stay PENDING and are NOT part of this approval. */}
            {awaiting.length > 0 && (
              <div className="mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-2.5">
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Not delivered yet — stays pending, not approved now
                </p>
                <div className="space-y-1.5">
                  {awaiting.map((p) => <ProofRow key={p.id} p={p} onZoom={setZoom} tone="pending" />)}
                </div>
              </div>
            )}

            {/* Voided lines — cancelled / refunded / couldn't-procure */}
            {voided.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {voided.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 rounded-lg border border-rose-100 bg-rose-50/50 px-3 py-2">
                    <FiRotateCcw className="h-3.5 w-3.5 text-rose-400" />
                    <span className="text-sm text-rose-500 line-through">{p.name}</span>
                    <span className="rounded bg-rose-100 px-1.5 py-px text-[9px] font-bold uppercase text-rose-600">
                      {p.osm_unavailable || p.return_state === 'osm_unavailable'
                        ? 'OSM unavailable'
                        : p.return_state === 'exchanged' ? 'Exchanged'
                        : p.return_state === 'returned' ? 'Returned' : 'Cancelled'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {!noPayment && shipped.length === 0 && alreadyOut.length < sellable.length && (
              <p className="mt-2 text-xs font-medium text-orange-600">
                Waiting for the supervisor to hand parts to the courier (with a receipt) before you can approve.
              </p>
            )}
          </div>

          {/* 3 — Approve */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">3 · Approve</p>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-600">
              <FiMapPin className="h-3.5 w-3.5 text-rose-400" /> Delivery address
            </label>
            <textarea
              rows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Where the parts are being delivered"
              className="mb-3 w-full resize-y rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
            />
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Dispatch note (optional)"
              className="mb-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
            />
            {err && <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>}
            <button
              type="button"
              onClick={approve}
              disabled={!canApprove || busy}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {busy ? <FiRefreshCw className="h-4 w-4 animate-spin" /> : <FiCheckCircle className="h-4 w-4" />}
              {shipped.length > 0
                ? `Approve & dispatch ${shipped.length} part${shipped.length === 1 ? '' : 's'}`
                : 'Approve & dispatch'}
            </button>
          </div>
        </div>
      </div>

      {/* Receipt zoom */}
      {zoom && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6" onClick={(e) => { e.stopPropagation(); setZoom(null); }}>
          <img src={zoom} alt="Delivery proof" className="max-h-[85vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

// Review flag styling — how urgently this customer needs the admin.
const REVIEW_STYLE = {
  warn: 'border-amber-200 bg-amber-50 text-amber-700',
  info: 'border-sky-200 bg-sky-50 text-sky-700',
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

// One customer — collapsed shows name · revenue · review flag; expanding
// reveals every order they have (no more duplicate rows for the same person).
const CustomerRow = ({ c, onOpen }) => {
  const rev = REVIEW_STYLE[c.review?.level] || REVIEW_STYLE.ok;
  const warn = c.review?.level === 'warn';
  const preview = c.orders_preview || [];
  return (
    <button
      type="button"
      onClick={() => onOpen(c)}
      className={`flex w-full items-center gap-3 rounded-2xl border bg-white p-4 text-left shadow-sm transition-all hover:border-violet-300 hover:shadow ${warn ? 'border-amber-200' : 'border-slate-200'}`}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-sm font-bold text-violet-600">
        {(c.customer_name || '?').slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-base font-bold text-slate-900">{c.customer_name || 'Customer'}</h3>
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">
            {c.orders_count} order{c.orders_count === 1 ? '' : 's'}
          </span>
        </div>
        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500">
          <FiPhone className="h-3 w-3 shrink-0 text-slate-400" /> {c.customer_phone || '—'}
        </p>
        {preview.length > 0 && (
          <p className="mt-1 truncate text-[11px] text-slate-400">
            {preview.map((o) => o.car).filter(Boolean).join(' · ')}{c.orders_count > preview.length ? ' …' : ''}
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <p className="text-sm font-black tabular-nums text-slate-900">{formatPrice(c.revenue) || '₹0'}</p>
        {c.review?.label ? (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${rev}`}>
            {warn && <FiAlertCircle className="h-3 w-3" />}
            {c.review.label}
          </span>
        ) : (
          <span className="text-[11px] font-medium text-slate-300">revenue</span>
        )}
      </div>
      <FiChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
    </button>
  );
};

const OrdersPage = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useState('pending');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ORDERS.LIST, {
        params: { tab, q: q.trim() || undefined },
      });
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load orders');
    } finally {
      setLoading(false);
    }
  }, [tab, q]);

  useEffect(() => { load(); }, [load]);

  // Open a customer's full history on its own screen (a customer can have
  // hundreds of orders — far too many for an inline dropdown).
  const openCustomer = (c) => navigate(`/orders/customer/${c.customer_id}`);

  const counts = {
    all: data?.all_count ?? 0,
    pending: data?.pending_count ?? 0,
    delivered: data?.delivered_count ?? 0,
  };
  const customers = data?.customers ?? [];

  return (
    <MainLayout>
      <div className="space-y-5">
        <PageHeader purpose="Grouped by customer — revenue, what needs review, and their full order history in one tap.">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:opacity-60"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </PageHeader>

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        {/* Tabs + search */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => {
              const active = tab === t.value;
              const Icon = t.icon;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTab(t.value)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                  <span className={`rounded-full px-1.5 text-xs font-bold ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                    {counts[t.value]}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Customer, phone, car, part, salesperson…"
              className="w-72 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
        </div>

        {/* List — one card per customer, expand for their orders */}
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
        ) : customers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 text-center">
            <FiShoppingCart className="mx-auto mb-2 h-8 w-8 text-slate-200" />
            <p className="text-sm text-slate-400">
              {tab === 'pending'
                ? 'No orders pending delivery'
                : tab === 'delivered'
                  ? 'Nothing delivered yet'
                  : q
                    ? 'No customers match your search'
                    : 'No inquiries yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {customers.map((c) => (
              <CustomerRow
                key={c.customer_id ?? c.customer_phone}
                c={c}
                onOpen={openCustomer}
              />
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default OrdersPage;
